import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';
import { accentBar, cardInteractive, eyebrowPill, gradients, hoverLift, iconTilePrimary } from '../../../theme/mixins';

export const getIconStyle = (theme: Theme): SxProps<Theme> => ({
    fontSize: 32,
});

export const iconTileStyles: SxProps<Theme> = {
    ...iconTilePrimary,
    mx: 'auto',
    mb: 3,
};

export const aboutWrapperStyles: SxProps<Theme> = {
    py: { xs: 6, md: 8 },
};

export const titleContainerStyles: SxProps<Theme> = {
    textAlign: 'center',
    mb: 8,
};

export const eyebrowStyles: SxProps<Theme> = eyebrowPill;

export const titleTextStyles = (theme: Theme): SxProps<Theme> => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.015em',
});

export const subtitleTextStyles: SxProps<Theme> = {
    maxWidth: 800,
    mx: 'auto',
    lineHeight: 1.6,
    color: 'text.secondary',
};

export const sectionTitleStyles: SxProps<Theme> = {
    fontWeight: 700,
    mb: 4,
    letterSpacing: '-0.01em',
};

/**
 * `min(300px, 100%)` rather than a bare `300px`, and that is the whole fix.
 *
 * A `minmax` floor is a hard floor: the track will not go below it even when its container
 * is narrower, so on a 320px phone -- where the layout's content box is 288px -- these
 * cards were 300px wide and pushed the page 12px sideways. Wrapping the floor in `min`
 * lets it collapse to the container's own width once the container is the smaller of the
 * two, which is what "as wide as 300px, or as wide as there is" actually means.
 */
export const featureGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
    gap: 4,
};

export const featureCardStyles: SxProps<Theme> = {
    height: '100%',
    p: 3,
    textAlign: 'center',
    overflow: 'hidden',
    ...cardInteractive,
};

export const featureAccentBarStyles: SxProps<Theme> = accentBar;

export const serviceGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
    gap: 3,
};

export const serviceCardStyles: SxProps<Theme> = {
    p: 3,
    textAlign: 'center',
    ...hoverLift,
};

export const callToActionStyles = (theme: Theme): SxProps<Theme> => ({
    textAlign: 'center',
    py: 6,
    px: 3,
    background: gradients.heroWash,
    borderRadius: 5,
    border: '1px solid rgba(16,24,40,0.06)',
});

// ------------------------------------------------------- the admin's edit mode

/** Right-aligned above the page, so Edit sits out of the reader's way. */
export const headerActionsStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'flex-end',
    mb: 3,
};

/**
 * Save and Cancel travel together with the hint that explains a disabled Save, and wrap
 * rather than crush on a phone.
 *
 * Pinned below the app bar for as long as edit mode lasts, the same treatment as the Our
 * Services editor: an admin who has scrolled down through the cards never has to travel
 * back up to commit, and the reason Save is unavailable stays on screen beside the button
 * instead of being a fact you can only discover by making the trip.
 *
 * `top: 72` is the app bar's md height (`layout/header.styles.ts`).
 */
export const editingActionsStyles: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 1.5,
    mb: 3,
    py: 1.5,
    px: 2,
    position: 'sticky',
    top: 72,
    zIndex: 10,
    backgroundColor: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: 999,
    border: '1px solid rgba(16,24,40,0.06)',
};

export const blockedSaveHintStyles: SxProps<Theme> = {
    color: 'error.main',
    fontWeight: 600,
};

export const alertStyles: SxProps<Theme> = {
    mb: 3,
};

export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 8,
};

export const emptyStateBoxStyles: SxProps<Theme> = {
    py: 8,
    textAlign: 'center',
};

export const emptyStateSubtitleStyles: SxProps<Theme> = {
    color: 'text.secondary',
};
